import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import api from 'api';
import { setAlert } from 'redux/alert';
import type { OrganizationInterface } from 'types';

/** Profile, agreements, and login all read this record for the same organization. */
export const ORGANIZATION_PROFILE_STALE_MS = 5 * 60 * 1000;

export const organizationProfileKeys = {
    all: ['organizationProfile'] as const,
    byServiceProvider: (serviceProviderId: string) =>
        ['organizationProfile', serviceProviderId] as const,
};

export const organizationProfileQuery = (serviceProviderId: string) => ({
    queryKey: organizationProfileKeys.byServiceProvider(serviceProviderId),
    queryFn: async (): Promise<OrganizationInterface> => {
        const response = await api.clientServiceProviders.getOrganizationProfile(serviceProviderId);
        return response.data;
    },
    staleTime: ORGANIZATION_PROFILE_STALE_MS,
    retry: false,
});

/**
 * One toast per failed fetch. A cached error should not alert again when another
 * screen mounts and observes the same failure.
 */
let lastReportedOrganizationProfileErrorAt = 0;

export interface UseOrganizationProfileOptions {
    enabled?: boolean;
    /** Profile page surfaces a toast. Agreements treat a missing profile as non-critical. */
    alertOnError?: boolean;
}

/**
 * GET /client-service-providers/:id/lmia
 * Cached per organization so profile and agreements do not each refetch it.
 */
export const useOrganizationProfile = (
    serviceProviderId?: string | null,
    options?: UseOrganizationProfileOptions
) => {
    const dispatch = useDispatch();
    const id = serviceProviderId ?? '';
    const alertOnError = options?.alertOnError ?? false;

    const query = useQuery({
        ...organizationProfileQuery(id),
        enabled: (options?.enabled ?? true) && Boolean(id),
    });
    const { errorUpdatedAt, isError, isFetching } = query;

    useEffect(() => {
        if (!alertOnError || !isError || isFetching || errorUpdatedAt === 0) {
            return;
        }
        if (lastReportedOrganizationProfileErrorAt === errorUpdatedAt) {
            return;
        }
        lastReportedOrganizationProfileErrorAt = errorUpdatedAt;
        dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
    }, [alertOnError, dispatch, errorUpdatedAt, isError, isFetching]);

    return query;
};
